import { END_POINTS } from '@config';
import ApiTags from '@constants/api-tags';
import { api } from '@core/baseApi';
import { BranchFeeCategoriesData } from '@features/branch-management/helpers/branchFeeCategories';
import {
  Branch,
  HTTPResponse,
  OrgAndBranchId,
  UpdateBranchRequest
} from '@types';

const extendedBaseQuery = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllBranches: builder.query<
      HTTPResponse<Branch>,
      { organizationId: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.BRANCH_MANAGEMENT.GET_ALL_BRANCHES(
          req.organizationId
        )}${req.queryString || ''}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.BRANCHES]
    }),
    createBranch: builder.mutation<HTTPResponse<any>, any>({
      query: (req) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.CREATE_BRANCH(req.organizationId),
        method: 'POST',
        body: req.body
      })
    }),
    onboardBranch: builder.mutation<HTTPResponse<any>, any>({
      query: (req) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.ONBOARD_BRANCH(req.params),
        method: 'PUT',
        body: req.body
      })
    }),
    updateBranch: builder.mutation<
      HTTPResponse<any>,
      { params: OrgAndBranchId; body: UpdateBranchRequest }
    >({
      query: (reqBody) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.UPDATE_BRANCH(reqBody.params),
        method: 'PATCH',
        body: reqBody.body
      })
    }),
    getOrgAccountDetails: builder.query<
      HTTPResponse<Branch>,
      { organizationId: string; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.ORGANIZATION.GET_ORGANIZATION_ACCOUNT_DETAILS(
          req.organizationId
        )}${req.queryString}`,
        method: 'GET'
      })
    }),
    getOrgAccountDetailsWithPagination: builder.query<
      HTTPResponse<Branch>,
      { organizationId: string; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.ORGANIZATION.GET_ORGANIZATION_ACCOUNT_DETAILS_WITH_PAGINATION(
          req.organizationId
        )}${req.queryString}`,
        method: 'GET'
      })
    }),
    getBranchById: builder.query<
      HTTPResponse<any>,
      { params: OrgAndBranchId; queryString: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.BRANCH_MANAGEMENT.GET_BRANCH_BY_ID(req.params)}${
          req.queryString
        }`,
        method: 'GET'
      })
    }),
    getBranchFeeCategories: builder.query<
      BranchFeeCategoriesData,
      OrgAndBranchId
    >({
      query: (params) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.GET_BRANCH_FEE_CATEGORIES(params),
        method: 'GET'
      }),
      providesTags: [ApiTags.BRANCH_FEE_CATEGORIES]
    }),
    getPresignedUrl: builder.query<
      HTTPResponse<any>,
      { organizationId: string; fileName: string }
    >({
      query: (req) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.GET_PRESIGNED_URL(
          req.organizationId,
          req.fileName
        ),
        method: 'GET'
      })
    }),
    linkBankDocument: builder.mutation<
      HTTPResponse<any>,
      { organizationId: string; body: any }
    >({
      query: (req) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.LINK_BANK_DOCUMENT(
          req.organizationId
        ),
        method: 'POST',
        body: req.body
      })
    }),
    getBankDetails: builder.query<
      HTTPResponse<any>,
      { organizationId: string; bankId: string }
    >({
      query: (req) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.GET_BANK_DETAILS(
          req.organizationId,
          req.bankId
        ),
        method: 'GET'
      })
    }),
    downloadBankDocument: builder.query<
      HTTPResponse<any>,
      { fileId: string; accessKey: string; queryString?: string }
    >({
      query: (req) => ({
        url: `${END_POINTS.BRANCH_MANAGEMENT.DOWNLOAD_BANK_DOCUMENT(
          req.fileId,
          req.accessKey
        )}${req.queryString || ''}`,
        method: 'GET'
      })
    }),
    deleteBankDocument: builder.mutation<
      HTTPResponse<any>,
      { organizationId: string; body: any }
    >({
      query: (req) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.DELETE_BANK_DOCUMENT(
          req.organizationId
        ),
        method: 'DELETE',
        body: req.body
      })
    }),
    getBranchConfig: builder.query<
      HTTPResponse<any>,
      OrgAndBranchId & { queryString?: string }
    >({
      query: ({ queryString = '', ...params }) => ({
        url: `${END_POINTS.BRANCH_MANAGEMENT.GET_BRANCH_CONFIG(
          params
        )}${queryString}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.BRANCH_CONFIG]
    }),
    updateBranchConfig: builder.mutation<
      HTTPResponse<any>,
      { params: OrgAndBranchId; body: any }
    >({
      query: (req) => ({
        url: END_POINTS.BRANCH_MANAGEMENT.UPDATE_BRANCH_CONFIG(req.params),
        method: 'PATCH',
        body: req.body
      }),
      invalidatesTags: [ApiTags.BRANCH_CONFIG, ApiTags.FORM_DETAIL]
    })
  })
});

// eslint-disable-next-line import/prefer-default-export
export const {
  useLazyGetAllBranchesQuery,
  useUpdateBranchMutation,
  useCreateBranchMutation,
  useOnboardBranchMutation,
  useGetOrgAccountDetailsQuery,
  useLazyGetOrgAccountDetailsQuery,
  useGetBranchByIdQuery,
  useLazyGetBranchByIdQuery,
  useLazyGetBranchFeeCategoriesQuery,
  useLazyGetOrgAccountDetailsWithPaginationQuery,
  useLazyGetPresignedUrlQuery,
  useLinkBankDocumentMutation,
  useLazyGetBankDetailsQuery,
  useLazyDownloadBankDocumentQuery,
  useDeleteBankDocumentMutation,
  useLazyGetBranchConfigQuery,
  useUpdateBranchConfigMutation
} = extendedBaseQuery;
